import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Icon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${fonts.dmSans.bold};
  ${transition('transform, text-decoration-color, color, background-color', 0.2)};

  --backgroundInitial: transparent;
  --borderWidth: 2px;
  --buttonHeight: 52px;
  --colorContrast: ${brand.global.white};
  --colorPrimary: ${brand.primary.blue};

  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: var(--borderWidth) solid var(--borderColor, var(--colorPrimary));
  background-color: var(--backgroundInitial);
  color: var(--colorPrimary);
  min-height: var(--buttonHeight);
  padding: ${fluid(4, 8)} ${fluid(16, 24)};
  text-decoration: underline;
  text-decoration-color: var(--textDecorationColor, transparent);
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
  user-select: none;
  width: auto;
  border-radius: 12px;

  &[data-fill='right'] {
    background-position: left center;
  }

  &[data-fill='left'] {
    background-position: right center;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    transition-duration: 0.15s;

    &:active {
      transform: scale(1.1) translateY(1.5px);

      /* Remove scale for text buttons */
      &[data-type='text-only'],
      &[data-type='text-only-inverted'] {
        transform: translateY(1.5px);
      }
    }
  }

  span& {
    button:focus-visible &,
    a:focus-visible &,
    button:hover &,
    a:hover & {
      background-color: var(--backgroundHover, var(--colorPrimary));
      color: var(--colorHover, var(--colorContrast));
      text-decoration-color: var(--textDecorationHoverColor, transparent);
    }
  }

  &:not(span) {
    cursor: pointer;

    &:focus-visible {
      outline-style: solid;
      outline-offset: 3px;
      outline-width: 2px;
      outline-color: currentColor;
    }

    &:focus-visible,
    &:hover {
      background-color: var(--backgroundHover, var(--colorPrimary));
      color: var(--colorHover, var(--colorContrast));
      text-decoration-color: var(--textDecorationHoverColor, transparent);
    }
  }

  /* Ensure the focus-visible */
  &:not(span)[data-icon-type='${Icon.ArrowRight}'] {
    &:focus-visible {
      outline-offset: 8px;
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    opacity: 0.65;
    background-size: 0% 100%;
    pointer-events: none;
  }
`;
export const SecondaryButtonStyles = css `
  --colorPrimary: ${brand.global.white};
  --colorContrast: ${brand.primary.blue};
`;
export const TertiaryButtonStyles = css `
  --borderColor: var(--colorContrast);
  --backgroundInitial: var(--colorContrast);
  --colorContrast: ${brand.primary.blue};
  --colorPrimary: ${brand.global.white};
`;
export const TextOnlyButtonStyles = css `
  --backgroundHover: transparent;
  --borderWidth: 0px;
  --buttonHeight: auto;
  --colorContrast: ${brand.global.white};
  --colorPrimary: ${brand.primary.blue};
  --colorHover: var(--colorPrimary);
  --textDecorationHoverColor: currentColor;

  padding-left: 0;
  padding-right: 0;
`;
export const TextOnlyInvertedButtonStyles = css `
  --backgroundHover: transparent;
  --borderWidth: 0px;
  --buttonHeight: auto;
  --colorContrast: ${brand.primary.blue};
  --colorPrimary: ${brand.global.white};
  --colorHover: var(--colorPrimary);
  --textDecorationHoverColor: currentColor;

  padding-left: 0;
  padding-right: 0;
`;
const Container = styled.button `
  ${DefaultButtonStyles};

  &[data-type='inverted'] {
    ${SecondaryButtonStyles};
  }

  &[data-type='solid'] {
    ${TertiaryButtonStyles};
  }

  &[data-type='text-only'] {
    ${TextOnlyButtonStyles};
  }

  &[data-type='text-only-inverted'] {
    ${TextOnlyInvertedButtonStyles};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
  }
`;
export const CommonButtonTextStyles = css `
  ${transition('color', 0.2)};
  ${fonts.dmSans.bold};

  color: currentColor;
  font-size: 16px;
  line-height: 1.25;
  text-decoration: none;
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color', 0.2)};

  --iconWidth: 16px;

  display: block;
  color: currentColor;
  margin: 0;
  padding: 0;

  > svg {
    display: block;
    width: var(--iconWidth);
  }

  [data-icon-position='left'] & {
    order: -1;
  }

  [data-icon-position='right'] & {
    order: 1;
  }

  [data-icon-only='true'] & {
    --iconWidth: 24px;

    color: currentColor;
    height: var(--iconWidth);
    margin: 0;
    padding: 0;
    width: var(--iconWidth);
  }

  /* "Grow" the arrow on hover */
  [data-icon-type='${Icon.ArrowRight}'] & {
    --moveAmount: 5px;

    svg {
      overflow: visible;
    }

    svg path {
      ${transition('transform', 0.2)};
    }

    button:focus-visible &,
    a:focus-visible &,
    button:hover &,
    a:hover & {
      svg {
        path {
          &[data-line] {
            transform: translateX(calc(var(--moveAmount) * -0.75)) scaleX(1.65);
          }

          &[data-head] {
            transform: translateX(var(--moveAmount));
          }
        }
      }
    }
  }

  [data-icon-type='${Icon.ArrowRight}']:hover &,
  [data-icon-type='${Icon.ArrowRight}']:focus-visible & {
    svg {
      path {
        &[data-line] {
          transform: translateX(calc(var(--moveAmount) * -0.75)) scaleX(1.65);
        }

        &[data-head] {
          transform: translateX(var(--moveAmount));
        }
      }
    }
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
